import React from 'react';
import { useParams } from 'react-router-dom';
import ArchitectureLayout from 'components/architecture/ArchitectureLayout';
import { getApiGroup, getEndpointById } from 'utils/architecture/apiEndpoints';

/**
 * Customer Portal API Endpoint Page
 * Displays detailed documentation for a specific endpoint
 */
const CustomerPortalEndpoint = () => {
  const { groupId, endpointId } = useParams();
  const group = getApiGroup('customer', groupId);
  const endpoint = getEndpointById('customer', groupId, endpointId);

  if (!group || !endpoint) {
    return (
      <ArchitectureLayout
        title="Endpoint Not Found"
        lastUpdated="2025-01-15"
        sections={[
          {
            id: 'error',
            title: 'Error',
            content: '<p>The requested endpoint could not be found.</p>'
          }
        ]}
      />
    );
  }

  // Generate example endpoint path (replace {param} with example values)
  const examplePath = endpoint.path
    .replace(/\{resource_id\}/g, '12345')
    .replace(/\{tenant_id\}/g, 'tenant-123')
    .replace(/\{user_id\}/g, 'user-456')
    .replace(/\{knowledge_id\}/g, 'knowledge-789')
    .replace(/\{cost_pool_id\}/g, 'pool-101')
    .replace(/\{ticket_id\}/g, 'ticket-202')
    .replace(/\{notification_id\}/g, 'notif-303')
    .replace(/\{role_id\}/g, 'role-404')
    .replace(/\{session_id\}/g, 'session-505');

  const sections = [
    {
      id: 'overview',
      title: endpoint.path,
      content: [
        `<p><strong>Description</strong>: ${endpoint.description}</p>`,
        `<p><strong>Methods</strong>: <code>${endpoint.methods.join('</code>, <code>')}</code></p>`,
        `<p><strong>Full Path</strong>: <code>${endpoint.path}</code></p>`,
        `<p><strong>Example Path</strong>: <code>${examplePath}</code></p>`,
        `<p><strong>Group</strong>: <a href="/architecture/processes/customer-portal/${groupId}">${group.name}</a></p>`
      ]
    },
    {
      id: 'authentication',
      title: 'Authentication',
      content: [
        '<p>This endpoint requires JWT authentication:</p>',
        '<ul>',
        '<li><strong>Token Source</strong>: Entra External ID</li>',
        '<li><strong>Header</strong>: <code>Authorization: Bearer YOUR_JWT_TOKEN</code></li>',
        '<li><strong>Tenant Scoping</strong>: All operations are automatically scoped to your tenant</li>',
        '</ul>'
      ]
    },
    {
      id: 'request',
      title: 'Request',
      content: [
        '<p>Request details depend on the HTTP method:</p>',
        ...endpoint.methods.map(method => {
          if (method === 'GET') {
            return `<h4>GET Request</h4><p>Retrieve data. No request body required. Query parameters may be available depending on the endpoint.</p>`;
          } else if (method === 'POST') {
            return `<h4>POST Request</h4><p>Create a new resource. Request body required in JSON format.</p>`;
          } else if (method === 'PUT') {
            return `<h4>PUT Request</h4><p>Update an existing resource. Request body required in JSON format.</p>`;
          } else if (method === 'PATCH') {
            return `<h4>PATCH Request</h4><p>Partially update a resource. Request body required in JSON format.</p>`;
          } else if (method === 'DELETE') {
            return `<h4>DELETE Request</h4><p>Delete a resource. No request body required.</p>`;
          }
          return '';
        })
      ]
    },
    {
      id: 'example',
      title: 'Example Request',
      content: [
        '<p>Example using cURL:</p>',
        '<pre><code>',
        `curl -X ${endpoint.methods[0]} "https://api.example.com/${endpoint.path.replace(/^api\//, '')}" \\`,
        '  -H "Authorization: Bearer YOUR_JWT_TOKEN"',
        endpoint.methods[0] !== 'GET' && endpoint.methods[0] !== 'DELETE' ? '  -H "Content-Type: application/json" \\\n  -d \'{"key": "value"}\'' : '',
        '</code></pre>'
      ].filter(Boolean)
    },
    {
      id: 'response',
      title: 'Response',
      content: [
        '<p>Response format:</p>',
        '<ul>',
        '<li><strong>Success</strong>: HTTP 200/201 with JSON response body</li>',
        '<li><strong>Error</strong>: HTTP 4xx/5xx with error details in JSON format</li>',
        '</ul>',
        '<p>For detailed response schemas, see the interactive API documentation.</p>'
      ]
    },
    {
      id: 'api-docs',
      title: 'Interactive Documentation',
      content: [
        '<p>For interactive API documentation with try-it-out functionality, visit:</p>',
        `<p><a href="/api-docs#/customer-portal/${group.id.toLowerCase()}">${group.name} API in Interactive Documentation →</a></p>`,
        '<p>You can test this endpoint, view request/response schemas, and see authentication requirements.</p>'
      ]
    },
    {
      id: 'navigation',
      title: 'Navigation',
      content: [
        `<p><a href="/architecture/processes/customer-portal/${groupId}">← Back to ${group.name} Group</a></p>`,
        '<p><a href="/architecture/processes/customer-portal">← Back to Customer Portal APIs</a></p>'
      ]
    }
  ];

  return (
    <ArchitectureLayout
      title={endpoint.path}
      lastUpdated="2025-01-15"
      sections={sections}
    />
  );
};

export default CustomerPortalEndpoint;

